import { Table } from '@tanstack/react-table';
import clsx from 'clsx';
import { FormattedMessage } from 'react-intl';

import ChevronLeft from '@/assets/icons/chevron-sm-left.svg';
import ChevronRight from '@/assets/icons/chevron-sm-right.svg';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Typography } from '@/components/ui/typography';
import { usePagination } from '@/hooks/use-pagination';

interface PaginationProps<TData> {
  table: Table<TData>;
}

export function Pagination<TData>({ table }: PaginationProps<TData>) {
  const { generatedPageNumbers } = usePagination(table);

  const { pageIndex, pageSize } = table.getState().pagination;
  const rowCount = table.getRowCount();
  const from = pageIndex * pageSize + 1;
  const to = Math.min((pageIndex + 1) * pageSize, rowCount);

  const setPage = (pageNumber: number | '...') => {
    if (pageNumber !== '...') table.setPageIndex(pageNumber - 1);
  };

  const handlePageInputChange = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key !== 'Enter') return;

    const value = parseInt(event.currentTarget.value, 10);

    if (value < 1 || value > table.getPageCount()) {
      // TODO: Show toaster to enter a valid page number
      return;
    }

    setPage(value);
  };

  return (
    <div className="flex justify-between">
      <div className="flex items-center gap-6">
        <Typography variant="subtitle1">{`${from} - ${to} of ${rowCount}`}</Typography>
        <div className="flex items-center gap-2">
          <Typography variant="subtitle1">
            <FormattedMessage
              defaultMessage="Go to page"
              description="Message shown for the go to page input"
            />
          </Typography>
          <Input
            type="text"
            className="!w-12"
            onKeyDown={handlePageInputChange}
          />
        </div>
      </div>
      <div className="flex gap-3">
        <Button
          variant="secondary"
          size="icon"
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
        >
          <img src={ChevronLeft} alt="Chevron left" />
        </Button>
        <div className="flex">
          {generatedPageNumbers.map((pageNumber, index) => {
            const isSelected =
              pageNumber === table.getState().pagination.pageIndex + 1;

            return (
              <Button
                // eslint-disable-next-line react/no-array-index-key
                key={`${pageNumber}-${index}`}
                variant="secondary"
                size="icon"
                onClick={() => setPage(pageNumber)}
                className={clsx(
                  'rounded-none first:ml-0 hover:relative hover:z-10',
                  index === 0 && 'rounded-l-lg',
                  index === generatedPageNumbers.length - 1 && 'rounded-r-lg',
                  !isSelected && '-ml-px',
                  isSelected &&
                    'bg-fx-brand-100 border-fx-primary relative z-10',
                  pageNumber === '...' && 'pointer-events-none cursor-auto',
                )}
              >
                <Typography
                  variant="subtitle1"
                  className={clsx(isSelected ? '!text-fx-brand-600' : '')}
                >
                  {pageNumber}
                </Typography>
              </Button>
            );
          })}
        </div>
        <Button
          variant="secondary"
          size="icon"
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
        >
          <img src={ChevronRight} alt="Chevron left" />
        </Button>
      </div>
    </div>
  );
}
